import { useState } from "react";
import { toast } from "sonner";
import type {
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Menu, MenuGroup, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import {
  useDemoteClaim,
  useEditClaim,
  usePromoteClaim,
  useRemoveClaim,
  useUndoClaimRemoval,
  whenWritten,
} from "@/hooks/use-claim-strategy";
import { toastWithUndo } from "@/components/ui/ToastSurface";
import { refusalDetail } from "@/services/api-client";
import { agree, listed, plural } from "@/lib/plural";
import { Refusal } from "./ChainControls";
import { latestRefusal } from "./latest-refusal";

/** What marking a claim as a placeholder does, said the same way wherever it is read. */
export const PLACEHOLDER_HELD =
  "Keeps the claim in the draft, flagged as not yet settled. It can't be confirmed until cleared.";
const CLEAR = "Treat this claim as settled again. It can then be confirmed.";
const NOT_UNDONE = "The undo did not reach the server.";
const SAME_CATEGORY =
  "Only claims in its own category are offered: a claim and the claim it depends on share a category.";
const TO_ANOTHER_INDEPENDENT =
  "Change its category on its tag to reach the others.";
const TO_ANOTHER_DEPENDENT =
  "To move it to another category, promote it first, then change its category on its tag.";

/** Every claim beneath the one named, however deep. */
export function claimsBeneath(
  claimId: string,
  stage: ClaimStrategyResponse,
): Set<string> {
  const found = new Set<string>();
  const frontier = [claimId];
  for (let at = frontier.pop(); at !== undefined; at = frontier.pop()) {
    for (const claim of stage.claims) {
      if (claim.parent_claim_id === at && !found.has(claim.claim_id)) {
        found.add(claim.claim_id);
        frontier.push(claim.claim_id);
      }
    }
  }
  return found;
}

/**
 * What removing a claim does to the positions its chain rests on, by count. A
 * position no claim left names returns to the positions not yet placed if it is
 * agreed now; one a claim left still names stays placed there.
 */
interface Reach {
  /** No claim left names it, and the holding area takes it: it is agreed now. */
  returning: number;
  /** No claim left names it, and it is not agreed at Thesis now. */
  lapsed: number;
  /** No claim left names it, and it is gone from the thesis. */
  deleted: number;
  /** A claim left still names it; ``holders`` are those claims' numbers, in claim order. */
  staying: number;
  holders: number[];
}

/** The reach of removing these claims, read against the claims left. */
function reachOf(goneClaims: ClaimResponse[], kept: ClaimResponse[]): Reach {
  // Each basis carries the server's own test of what the holding area takes.
  const bases = new Map(
    goneClaims.flatMap((c) => c.bases.map((b) => [b.position_id, b] as const)),
  );
  const held = new Set(kept.flatMap((c) => c.bases.map((b) => b.position_id)));
  const unheld = [...bases.values()].filter((b) => !held.has(b.position_id));
  return {
    returning: unheld.filter((b) => b.confirmed).length,
    lapsed: unheld.filter((b) => !b.confirmed && !b.removed).length,
    deleted: unheld.filter((b) => b.removed).length,
    staying: bases.size - unheld.length,
    holders: kept
      .filter((c) => c.bases.some((b) => bases.has(b.position_id)))
      .map((c) => c.number),
  };
}

/**
 * Where each position goes, in one set of words for the confirmation (before)
 * and the toast (after). Every sentence is said only when it has a position.
 */
function whereTheyGo(reach: Reach, when: "before" | "after"): string[] {
  const { returning, lapsed, deleted, staying, holders } = reach;
  const said: string[] = [];
  if (returning > 0) {
    said.push(
      when === "before"
        ? `${plural(returning, "position")} ${agree(returning, "returns", "return")} to the positions not yet placed.`
        : `${plural(returning, "position")} moved to Not yet placed.`,
    );
  }
  const not = (n: number, why: string) =>
    `${plural(n, "position")} ${agree(n, "does", "do")} not return, because ${agree(n, "it", "they")} ${why}.`;
  if (lapsed > 0) {
    said.push(
      not(lapsed, `${agree(lapsed, "is", "are")} not agreed at Thesis now`),
    );
  }
  if (deleted > 0) {
    said.push(
      not(deleted, `${agree(deleted, "was", "were")} removed from the thesis`),
    );
  }
  if (staying > 0) {
    const claims = `${agree(holders.length, "claim", "claims")} ${listed(holders.map(String))}`;
    const which = `, which still ${agree(holders.length, "rests", "rest")} on ${agree(staying, "it", "them")}`;
    said.push(
      `${plural(staying, "position")} ${agree(staying, "stays", "stay")} placed in ${claims}${when === "before" ? which : ""}.`,
    );
  }
  return said;
}

/** What removing a claim takes with it, in the words the confirmation reads. */
function removal(goneClaims: ClaimResponse[], kept: ClaimResponse[]): string {
  const dependents = goneClaims.length - 1;
  const chain =
    dependents > 0
      ? [
          `Its ${plural(dependents, "dependent claim")} ${agree(dependents, "goes", "go")} with it.`,
        ]
      : [];
  const where = whereTheyGo(reachOf(goneClaims, kept), "before");
  const none = ["It rests on no position, so no position moves."];
  return [...chain, ...(where.length > 0 ? where : none)].join(" ");
}

/**
 * What the removal did, in the toast's words: how many positions the served
 * tray gained, and the claims the server kept that still hold the rest, by the
 * numbers they now have.
 */
function removed(
  claim: ClaimResponse,
  goneClaims: ClaimResponse[],
  served: ClaimStrategyResponse,
  back: number,
): string {
  const after = { ...reachOf(goneClaims, served.claims), returning: back };
  return [
    `Claim ${String(claim.number)} removed.`,
    ...whereTheyGo(after, "after"),
  ].join(" ");
}

/**
 * The claims a claim can be made to depend on: any other claim of its category
 * that is not beneath it and not already its parent. Its category is changed
 * on its tag.
 */
function parentsFor(claim: ClaimResponse, stage: ClaimStrategyResponse) {
  const beneath = claimsBeneath(claim.claim_id, stage);
  return stage.claims.filter(
    (c) =>
      c.form_id === claim.form_id &&
      c.claim_id !== claim.claim_id &&
      c.claim_id !== claim.parent_claim_id &&
      !beneath.has(c.claim_id),
  );
}

/** Mark or clear the placeholder, and say which once the write lands. */
function markPlaceholder(
  edit: ReturnType<typeof useEditClaim>,
  claimId: string,
  placeholder: boolean,
) {
  edit.mutate(
    { claimId, body: { placeholder } },
    {
      onSuccess: () => {
        toast(
          placeholder
            ? "Marked as a placeholder, not yet final."
            : "Placeholder cleared.",
        );
      },
    },
  );
}

/**
 * Where the claim can go: each claim it could depend on.
 *
 * A child of the menu rather than part of it, because `Menu` mounts its
 * children only while it is open. Read in the menu's body, every closed claim
 * in the set would walk the whole set and build an item for each destination on
 * every render, and a drag re-renders the set once per row the pointer crosses.
 */
function Destinations({
  claim,
  stage,
  onDemote,
}: {
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  onDemote: (parentClaimId: string) => void;
}) {
  const parents = parentsFor(claim, stage).map((parent) => (
    <MenuItem
      key={parent.claim_id}
      title={
        claim.kind === "independent"
          ? `Make dependent of claim ${String(parent.number)}`
          : `Move under claim ${String(parent.number)}`
      }
      onSelect={() => {
        onDemote(parent.claim_id);
      }}
    />
  ));
  // A claim and its parent share a category, so the claims of another category are never
  // offered; the menu says why rather than leave the attorney to wonder where they went.
  if (!stage.claims.some((c) => c.form_id !== claim.form_id)) return parents;
  return (
    <MenuGroup
      heading={
        claim.kind === "independent" ? "Make dependent" : "Move under a claim"
      }
      description={`${SAME_CATEGORY} ${claim.kind === "independent" ? TO_ANOTHER_INDEPENDENT : TO_ANOTHER_DEPENDENT}`}
    >
      {parents}
    </MenuGroup>
  );
}

/**
 * The acts that reshape one claim, in the design's menu: mark or clear it as a
 * placeholder, promote a dependent, or make a claim depend on another claim of
 * its category. A claim's category is changed on its tag. A refusal stays on
 * screen in the server's words once the menu has closed.
 */
export function ClaimOptions({
  taskId,
  claim,
  stage,
  onEditText,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  /** Put the caret in the claim's words, which are written in where they are drawn. */
  onEditText: () => void;
}) {
  const edit = useEditClaim(taskId);
  const promote = usePromoteClaim(taskId);
  const demote = useDemoteClaim(taskId);
  const { refused, starting } = latestRefusal([edit, promote, demote]);
  const id = claim.claim_id;
  const placeholder = !claim.placeholder;
  return (
    <>
      <Menu name={`Options for claim ${String(claim.number)}`}>
        <MenuItem
          title="Edit text"
          description="Puts the cursor in the claim's words. Write in them where they are."
          onSelect={onEditText}
        />
        <MenuItem
          title={
            claim.placeholder ? "Clear placeholder" : "Mark as placeholder"
          }
          description={claim.placeholder ? CLEAR : PLACEHOLDER_HELD}
          onSelect={() => {
            starting(edit, () => {
              markPlaceholder(edit, id, placeholder);
            });
          }}
        />
        <MenuSeparator />
        {claim.kind === "dependent" && (
          <MenuItem
            title="Promote to independent"
            description="Makes this claim an independent claim of its form, its words unchanged."
            onSelect={() => {
              starting(promote, () => {
                promote.mutate(id);
              });
            }}
          />
        )}
        <Destinations
          claim={claim}
          stage={stage}
          onDemote={(parentClaimId) => {
            starting(demote, () => {
              demote.mutate({ claimId: id, parentClaimId });
            });
          }}
        />
      </Menu>
      {refused !== undefined && <Refusal error={refused.error} />}
    </>
  );
}

/**
 * Remove a claim after asking, naming what goes with it and where each of its
 * positions ends up. Once it is gone, says which positions came back to the
 * tray, so the tray can show them, and offers Undo. The row that draws this is
 * gone by the time the answer lands, so the toast and the undo run off the
 * write's own promise: a mutate callback would be dropped with the row.
 */
export function RemoveClaim({
  taskId,
  claim,
  stage,
  onReturned,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  onReturned: (positionIds: string[]) => void;
}) {
  const [asking, setAsking] = useState(false);
  // The claims the removal takes, read when the confirmation opens rather than
  // on every render (every row draws this, and a drag redraws them all), and
  // kept while the dialog closes, so its words do not vanish during the fade.
  const [gone, setGone] = useState<ClaimResponse[]>([]);
  const remove = useRemoveClaim(taskId);
  const undo = useUndoClaimRemoval(taskId);
  const undone = () => {
    undo.mutateAsync(claim.claim_id).then(
      () => toast(`Claim ${String(claim.number)} is back.`),
      (error: unknown) => toast(refusalDetail(error, NOT_UNDONE)),
    );
  };
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="dense"
        disabled={remove.isPending}
        title="Remove this claim; a position no other claim rests on returns to the unplaced list"
        onClick={() => {
          const taken = claimsBeneath(claim.claim_id, stage).add(
            claim.claim_id,
          );
          setGone(stage.claims.filter((c) => taken.has(c.claim_id)));
          setAsking(true);
        }}
      >
        Remove
      </Button>
      <ConfirmDialog
        open={asking}
        title={`Remove claim ${String(claim.number)}?`}
        description={removal(
          gone,
          stage.claims.filter((c) => !gone.includes(c)),
        )}
        actionLabel="Remove"
        destructive
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          setAsking(false);
          // What was waiting before the write, to tell the positions this
          // removal returns from the ones already there.
          const before = new Set(stage.unplaced.map((p) => p.position_id));
          whenWritten(remove.mutateAsync(claim.claim_id), (served) => {
            const back = served.unplaced
              .map((p) => p.position_id)
              .filter((positionId) => !before.has(positionId));
            onReturned(back);
            toastWithUndo(removed(claim, gone, served, back.length), undone);
          });
        }}
      />
      {remove.isError && <Refusal error={remove.error} />}
    </>
  );
}
